/**
 * LiveTranscriptScroller Component
 * ORCA360 - Synchronized Transcript Display for Speaker Role Assignment
 *
 * Replaces the static sample text with a karaoke-style scrolling transcript
 * that auto-scrolls in sync with audio playback, highlighting the card's
 * speaker and showing other speakers' text for conversation context.
 */

import { useEffect, useRef, useMemo } from 'react';
import type { AudioPlaybackState } from '../hooks/useAudioSnippetPlayer';

export interface TranscriptSegment {
  id: string;
  speaker_label: string;
  start_time: string;
  end_time: string;
  start_ms: number | null;
  end_ms: number | null;
  text: string;
}

interface LiveTranscriptScrollerProps {
  allSegments: TranscriptSegment[];
  speakerLabel: string;
  playbackState: AudioPlaybackState;
  speakerSnippetIds: Set<string>;
}

export function LiveTranscriptScroller({
  allSegments,
  speakerLabel,
  playbackState,
  speakerSnippetIds,
}: LiveTranscriptScrollerProps) {
  const activeSegmentRef = useRef<HTMLDivElement>(null);

  // Determine if this speaker's card has the active snippet
  const isActivePlayer =
    playbackState.activeSnippetId != null &&
    speakerSnippetIds.has(playbackState.activeSnippetId);

  const isLive = isActivePlayer && (playbackState.isPlaying || playbackState.progress > 0);

  // Select segments to display
  const visibleSegments = useMemo(() => {
    if (!isLive || !playbackState.snippetStartMs) {
      // Static preview: first 3 segments of this speaker
      return allSegments
        .filter((s) => s.speaker_label === speakerLabel)
        .slice(0, 3);
    }

    // Live mode: show all speakers in a time window around the snippet
    const windowStart = playbackState.snippetStartMs - 30_000;
    const windowEnd = playbackState.snippetEndMs + 90_000;

    return allSegments
      .filter((s) => s.start_ms != null && s.start_ms >= windowStart && s.start_ms <= windowEnd)
      .slice(0, 50);
  }, [allSegments, speakerLabel, isLive, playbackState.snippetStartMs, playbackState.snippetEndMs]);

  // Find the currently-spoken segment (throttled to ~5Hz)
  const roundedTimeMs = Math.floor(playbackState.absoluteTimeMs / 200) * 200;

  const activeSegmentId = useMemo(() => {
    if (!isLive) return null;

    // First: exact match (current time falls within segment range)
    for (const seg of visibleSegments) {
      if (seg.start_ms != null && seg.end_ms != null) {
        if (roundedTimeMs >= seg.start_ms && roundedTimeMs <= seg.end_ms) {
          return seg.id;
        }
      }
    }

    // Fallback: closest segment by start_ms
    let closestId: string | null = null;
    let closestDist = Infinity;
    for (const seg of visibleSegments) {
      if (seg.start_ms == null) continue;
      const dist = Math.abs(roundedTimeMs - seg.start_ms);
      if (dist < closestDist) {
        closestDist = dist;
        closestId = seg.id;
      }
    }
    return closestId;
  }, [visibleSegments, isLive, roundedTimeMs]);

  // Auto-scroll to the active segment
  useEffect(() => {
    if (activeSegmentRef.current && isLive) {
      activeSegmentRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [activeSegmentId, isLive]);

  if (visibleSegments.length === 0) {
    return (
      <div className="mb-3 p-2 bg-gray-50 rounded text-sm text-gray-400 italic">
        No transcript segments available
      </div>
    );
  }

  // Static preview mode
  if (!isLive) {
    return (
      <div className="mb-3 p-2 bg-gray-50 rounded text-sm text-gray-700 italic max-h-[100px] overflow-y-auto">
        {visibleSegments.map((seg) => (
          <p key={seg.id} className="mb-1 last:mb-0">
            <span className="font-mono text-[10px] text-steel/50 mr-1">
              [{seg.start_time}]
            </span>
            {seg.text.length > 120 ? seg.text.substring(0, 120) + '...' : seg.text}
          </p>
        ))}
      </div>
    );
  }

  // Live scrolling mode
  return (
    <div className="mb-3 rounded-lg border border-steel/20 bg-gray-50 overflow-y-auto max-h-[140px]">
      <div className="p-2 space-y-0.5">
        {visibleSegments.map((seg) => {
          const isActive = seg.id === activeSegmentId;
          const isTargetSpeaker = seg.speaker_label === speakerLabel;

          return (
            <div
              key={seg.id}
              ref={isActive ? activeSegmentRef : undefined}
              className={`px-2 py-1 rounded text-sm leading-relaxed transition-colors duration-150 ${
                isActive
                  ? 'bg-orca-elevate/8 border-l-2 border-l-orca-elevate'
                  : ''
              } ${
                isTargetSpeaker
                  ? 'text-navy'
                  : 'text-steel/50 italic'
              }`}
            >
              <span className={`font-mono text-[10px] mr-1 ${
                isTargetSpeaker ? 'text-navy/40' : 'text-steel/30'
              }`}>
                [{seg.start_time}]
              </span>
              {!isTargetSpeaker && (
                <span className="text-[10px] font-semibold text-steel/40 mr-1">
                  {seg.speaker_label}:
                </span>
              )}
              <span className={isActive ? 'font-medium' : ''}>
                {seg.text}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
